import React from 'react';
import Cookies from 'js-cookie';
import { Box, Stack } from '@mui/material';
import { motion } from 'framer-motion';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';
import EditorialButton from '../components/editorial/EditorialButton';

import { classbyId } from '../api/classes/classesData';
import SocialLoginModal from '../components/modal/SocialLoginModal';
import { googleAuth, appleAuth } from '../service/socialAuth';
import { updateUserPhone } from '../api/users/saveUsers';
import useFirebaseUser from '../hooks/useFirebaseUser';

const containsHTML = (str) => /<\/?[a-z][\s\S]*>/i.test(str || '');

export default function ClassDetail() {
  const [classData, setClassData] = React.useState({});
  const [open, setOpen] = React.useState(false);
  const { user } = useFirebaseUser();
  const uid = user?.uid;

  React.useEffect(() => {
    (async () => {
      const classId = localStorage.getItem('classId');
      const res = await classbyId(classId);
      if (res?.status === 200) setClassData(res.data);
    })();
  }, []);

  const handleGoogle = async () => {
    try {
      const res = await googleAuth();
      if (res?.needsLink) return res;
      return res?.uid ? res : null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  const handleApple = async () => {
    try {
      const res = await appleAuth();
      if (res?.needsLink) return res;
      return res?.uid ? res : null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  const handleAccountCreated = async (res) => {
    if (res?.uid) {
      Cookies.set('uid', res.uid);
      Cookies.set('email', res.email);
      if (res.customerStripeID) Cookies.set('customerStripeID', res.customerStripeID);
      if (res.phoneNumber && res.phoneVerified) {
        try {
          await updateUserPhone(res.uid, res.phoneNumber, true);
          Cookies.set('phoneVerified', 'true');
        } catch (e) {
          console.error('Failed to update phone:', e);
        }
      }
    }
  };

  const handleReserve = () => {
    localStorage.setItem('amount', classData?.classFee);
    localStorage.setItem('paymentType', 'Class');
    localStorage.setItem('paymentTypeName', classData?.classTitle);
    if (!uid) setOpen(true);
  };

  const reserveHref = classData?.classFee !== '' && uid ? '/payment-checkout' : null;

  return (
    <EditorialPageShell title={classData?.classTitle || 'Class'} description={classData?.description}>
      <PageHero
        kicker="Class  ·  Detail"
        meta={classData?.duration || 'Weekly shiur'}
        title={classData?.classTitle || 'A shiur at KBT'}
        subtitle="A piece of the weekly rhythm. Sit at the table, take a sefer, find your chevruta."
        image="/assets/images/kbt/shiur.jpg"
        height="74vh"
      />

      <EditorialSection
        kicker="About this class"
        title="What you'll learn,"
        titleAccent="and how it runs."
        watermark="ש"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
            gap: { xs: 6, md: 10 },
            alignItems: 'start',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <Stack spacing={4}>
              <Box
                sx={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 17,
                  lineHeight: 1.7,
                  color: 'var(--text)',
                  opacity: 0.88,
                  '& a': { color: 'var(--teal-500)' },
                }}
              >
                {containsHTML(classData?.description) ? (
                  <div dangerouslySetInnerHTML={{ __html: classData.description }} />
                ) : (
                  classData?.description || ' '
                )}
              </Box>

              {classData?.PaymentType !== 'free' && classData?.classFee != null && (
                <Stack direction="row" spacing={2} alignItems="baseline">
                  <Box
                    sx={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 700,
                      fontSize: { xs: 38, md: 52 },
                      color: 'var(--teal-500)',
                      lineHeight: 1,
                    }}
                  >
                    ${classData?.classFee}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 400,
                      fontSize: { xs: 24, md: 30 },
                      color: 'var(--navy-200)',
                      textDecoration: 'line-through',
                      lineHeight: 1,
                    }}
                  >
                    ${Number(classData?.classFee || 0) + 50}
                  </Box>
                </Stack>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <EditorialButton
                  variant="primary"
                  onClick={handleReserve}
                  href={reserveHref}
                >
                  Reserve Class
                </EditorialButton>
                <EditorialButton variant="ghost" href="/contact-us">
                  Ask a Question
                </EditorialButton>
              </Stack>
            </Stack>
          </motion.div>

          {/* Detail panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box
              sx={{
                background: 'var(--navy-900)',
                color: '#fff',
                p: { xs: 4, md: 5 },
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  right: '-10%',
                  bottom: '-35%',
                  fontFamily: 'var(--font-hebrew)',
                  fontSize: 480,
                  color: '#fff',
                  opacity: 0.04,
                  lineHeight: 0.8,
                  pointerEvents: 'none',
                }}
              >
                ת
              </Box>

              <Stack spacing={4} sx={{ position: 'relative' }}>
                {[
                  ['Ages', classData?.ages || 'Adults'],
                  ['Location', classData?.location || 'KBT, 9017 W. Pico Blvd.'],
                  ['Duration', classData?.duration],
                ]
                  .filter(([, v]) => Boolean(v))
                  .map(([k, v]) => (
                    <Stack key={k} spacing={1}>
                      <Box
                        sx={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 10.5,
                          letterSpacing: '0.22em',
                          textTransform: 'uppercase',
                          color: 'var(--teal-300)',
                        }}
                      >
                        {k}
                      </Box>
                      <Box
                        sx={{
                          fontFamily: 'var(--font-display)',
                          fontWeight: 500,
                          fontSize: { xs: 19, md: 22 },
                          color: '#fff',
                          lineHeight: 1.3,
                        }}
                      >
                        {v}
                      </Box>
                    </Stack>
                  ))}
                <Box sx={{ pt: 2, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                  <EditorialButton variant="inverse" href="/contact-us" fullWidth>
                    Contact us
                  </EditorialButton>
                </Box>
              </Stack>
            </Box>
          </motion.div>
        </Box>
      </EditorialSection>

      <SocialLoginModal
        open={open}
        handleClose={() => setOpen(false)}
        googleLogin={handleGoogle}
        appleLogin={handleApple}
        onAccountCreated={handleAccountCreated}
        requirePhoneVerification={true}
      />
    </EditorialPageShell>
  );
}
